import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowRight, Check } from 'lucide-react'
import { DARK, TEAL, BG, MarketingNavBar, MarketingFooter, FaqAccordion, faqJsonLd } from '../../components/MarketingLayout'

const FAQS = [
  {
    q: 'Elég csak a HACCP-tervet megírni, a napi naplózás nem fontos?',
    a: 'Nem elég. Az ellenőrzés nem csak a HACCP-tervet, hanem a napi vezetett naplókat (hűtő hőmérséklet, takarítás, termelés) is kéri — a terv papíron jó, de bizonyíték nélkül az ellenőr azt feltételezi, hogy nem tartjátok be.',
  },
  {
    q: 'Kell-e gyártmánylap, ha csak a saját boltomban árulok, sehova nem szállítok?',
    a: 'Ha kizárólag a saját üzletedben, közvetlenül a végfogyasztónak értékesítesz és nem szállítasz tovább, sok esetben elég az egyszerűbb anyaghányad-nyilvántartás a teljes gyártmánylap helyett — de ha bármikor elkezdesz szállítani (viszonteladónak, rendezvényre, közétkeztetésbe), onnantól a teljes gyártmánylap kötelező. Érdemes már most úgy vezetni az adatokat, hogy bármikor átválthass.',
  },
  {
    q: 'Mennyi idő ezt mind kézzel, Excelben vezetni?',
    a: 'Recepttípusonként több óra, mert a tápértéket, allergéneket és a jogszabályi mezőket minden receptváltozásnál újra kell számolni. Ez az az ismétlődő munka, amit a CukrászDoc automatizál: egyszer viszed fel a receptet, utána minden dokumentum (gyártmánylap, HACCP napló, allergén címke, eredetigazolás) ugyanabból az adatból generálódik.',
  },
]

const TOC = [
  { id: 'gyartmanylap', label: '1. Gyártmánylap vagy anyaghányad-nyilvántartás?' },
  { id: 'haccp', label: '2. HACCP-rendszer és napi napló' },
  { id: 'allergen', label: '3. Allergén jelölés' },
  { id: 'eredetigazolas', label: '4. Eredetigazolás közétkeztetésbe, rendezvényre' },
  { id: 'tapertek', label: '5. Tápérték jelölés csomagolt terméknél' },
  { id: 'bejelentes', label: '6. NÉBIH bejelentés, működési engedély' },
  { id: 'checklist', label: 'Összefoglaló checklist' },
]

export default function CukraszdaNyitasBlogPage() {
  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Helmet>
        <title>Cukrászda nyitás dokumentációja 2026: checklist — CukrászDoc</title>
        <meta name="description" content="Gyártmánylap, HACCP, allergén jelölés, eredetigazolás, NÉBIH bejelentés — összefoglaló checklist, mire lesz szükséged dokumentáció szempontból, mielőtt kinyitod a cukrászdát vagy pékséget." />
        <link rel="canonical" href="https://cukraszdoc.hu/blog/cukraszda-nyitas-dokumentacio" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd(FAQS))}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: 'Cukrászda nyitás dokumentációja: gyártmánylap, HACCP, allergén és engedélyek — checklist',
          datePublished: '2026-07-28',
          dateModified: '2026-07-28',
          author: { '@type': 'Organization', name: 'CukrászDoc' },
          publisher: { '@type': 'Organization', name: 'CukrászDoc' },
        })}</script>
      </Helmet>
      <MarketingNavBar />

      <section style={{ background: `linear-gradient(160deg, ${DARK} 0%, #162119 100%)`, paddingTop: '5rem' }} className="relative overflow-hidden">
        <div className="relative max-w-3xl mx-auto px-6 py-16 lg:py-20">
          <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ background: 'rgba(91,184,160,0.15)', color: TEAL, border: '1px solid rgba(91,184,160,0.3)' }}>
            Tudástár
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold leading-tight text-white mb-5">
            Cukrászda nyitás dokumentációja: <span style={{ color: TEAL }}>mire lesz szükséged 2026-ban</span>
          </h1>
          <p className="text-lg leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Gyártmánylap, HACCP-terv, allergén jelölés, eredetigazolás, bejelentési kötelezettség — összeszedtük, mi micsoda, és mikor melyikre lesz ténylegesen szükséged cukrászda, pékség vagy catering vállalkozás indításakor.
          </p>
        </div>
        <div className="relative" style={{ height: 60, marginBottom: -2 }}>
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="absolute bottom-0 w-full h-full">
            <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="#ffffff" />
          </svg>
        </div>
      </section>

      <section className="bg-white pt-14 pb-6">
        <div className="max-w-3xl mx-auto px-6">
          <div className="rounded-2xl p-6" style={{ background: BG, border: '1px solid rgba(91,184,160,0.2)' }}>
            <p className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: DARK, letterSpacing: '0.05em' }}>Tartalom</p>
            <ul className="flex flex-col gap-1.5">
              {TOC.map(({ id, label }) => (
                <li key={id}>
                  <a href={`#${id}`} className="text-sm hover:underline" style={{ color: DARK }}>{label}</a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <article className="bg-white pb-16">
        <div className="max-w-3xl mx-auto px-6 flex flex-col gap-14">

          <div>
            <p className="text-base leading-relaxed text-gray-600">
              Amikor cukrászdát, pékséget vagy catering vállalkozást indítasz, a dokumentáció könnyen az utolsó helyre kerül a bútor, a gép és a beszállítók mögött — pedig egy ellenőrzésnél pont ez az, amit először kérnek. Az alábbi hat terület fedi le a leggyakoribb dokumentációs kötelezettségeket. Egyik sem helyettesíti a HACCP-tanácsadóval vagy a NÉBIH-hel való egyeztetést a te konkrét profilodra, de jó kiindulópont, hogy tudd, mit kell egyáltalán megkérdezni.
            </p>
          </div>

          <div id="gyartmanylap">
            <h2 className="text-2xl font-bold mb-4" style={{ color: DARK }}>1. Gyártmánylap vagy anyaghányad-nyilvántartás?</h2>
            <p className="text-base leading-relaxed text-gray-600 mb-4">
              A 2008. évi XLVI. törvény (Éltv.) szerint minden élelmiszer-vállalkozás, amely az élelmiszert előállítja vagy forgalomba hozza, köteles írásos gyártmánylapot vezetni — receptúrával, összetevőkkel, allergénjelöléssel, tápértékkel és tárolási előírással. Ha viszont kizárólag a saját üzletedben, közvetlenül a végfogyasztónak értékesítesz és nem szállítasz tovább, sok esetben elegendő az egyszerűbb anyaghányad-nyilvántartás.
            </p>
            <p className="text-base leading-relaxed text-gray-600">
              A gyakorlati tanulság: ha van esély rá, hogy egyszer szállítasz majd (rendezvényre, viszonteladónak, közétkeztetésbe), érdemes már az induláskor teljes gyártmánylapot vezetni — utólag minden receptet visszamenőleg kiegészíteni sokkal több munka. Részletek: <Link to="/gyartmanylap" style={{ color: TEAL, fontWeight: 600 }}>gyártmánylap készítés</Link>.
            </p>
          </div>

          <div id="haccp">
            <h2 className="text-2xl font-bold mb-4" style={{ color: DARK }}>2. HACCP-rendszer és napi napló</h2>
            <p className="text-base leading-relaxed text-gray-600 mb-4">
              A 852/2004/EK rendelet alapján minden élelmiszer-előállító és -forgalmazó egységnek HACCP-alapú élelmiszerbiztonsági rendszert kell működtetnie: veszélyelemzés, kritikus szabályozási pontok (jellemzően a hőkezelés és a hűtés cukrászatban), valamint napi ellenőrzési napló.
            </p>
            <p className="text-base leading-relaxed text-gray-600">
              A leggyakoribb hiba nem a terv hiánya, hanem hogy a napi napló nincs vezetve — egy jól megírt HACCP-terv önmagában nem bizonyíték, ha nincs mögötte folyamatosan vezetett hőmérséklet- és takarítási napló. Részletek: <Link to="/haccp-igazolas" style={{ color: TEAL, fontWeight: 600 }}>HACCP igazolás és napló</Link>.
            </p>
          </div>

          <div id="allergen">
            <h2 className="text-2xl font-bold mb-4" style={{ color: DARK }}>3. Allergén jelölés</h2>
            <p className="text-base leading-relaxed text-gray-600 mb-4">
              2014. december 13. óta az EU 1169/2011 rendelet alapján a nem előrecsomagolt élelmiszerekre — tehát a pult mögül, darabra eladott sütire, tortára is — kötelező az allergén-információ biztosítása, írásban vagy dokumentált szóbeli tájékoztatással.
            </p>
            <p className="text-base leading-relaxed text-gray-600">
              A gyakorlatban a legegyszerűbb, ha az allergén-adatok a gyártmánylapból automatikusan bekerülnek egy nyomtatható táblázatba vagy címkére, ahelyett hogy minden termékhez külön kellene utánanézned az alapanyagoknak. Részletek: <Link to="/allergen-cimke-nyomtatas" style={{ color: TEAL, fontWeight: 600 }}>allergén jelölés és címke</Link>.
            </p>
          </div>

          <div id="eredetigazolas">
            <h2 className="text-2xl font-bold mb-4" style={{ color: DARK }}>4. Eredetigazolás közétkeztetésbe, rendezvényre</h2>
            <p className="text-base leading-relaxed text-gray-600 mb-4">
              Ha óvodának, iskolának, bölcsődének vagy rendezvényre szállítasz saját készítésű terméket, a megrendelő tipikusan kér egy eredetigazolást (a köznyelvben gyakran "származási igazolás") minden szállított tételhez — ez a nyomonkövethetőségi kötelezettséged (852/2004/EK) igazolása papíron: ki, hol és mikor állította elő a terméket.
            </p>
            <p className="text-base leading-relaxed text-gray-600">
              Ez nem külön rendszer, hanem a HACCP-dokumentációdból és a gyártmánylapból már ismert adatok egy szállításonkénti kivonata. Részletek: <Link to="/szarmazasi-igazolas" style={{ color: TEAL, fontWeight: 600 }}>eredetigazolás óvodának, iskolának</Link>.
            </p>
          </div>

          <div id="tapertek">
            <h2 className="text-2xl font-bold mb-4" style={{ color: DARK }}>5. Tápérték jelölés csomagolt terméknél</h2>
            <p className="text-base leading-relaxed text-gray-600 mb-4">
              Ha a terméket előrecsomagolva, saját márkanévvel értékesíted (pl. webshopban, viszonteladónak polcra), az EU 1169/2011 rendelet szerinti tápértéktáblázat (energia, zsír, telített zsír, szénhidrát, cukor, fehérje, só) kötelező a csomagoláson vagy a hozzá tartozó dokumentáción.
            </p>
            <p className="text-base leading-relaxed text-gray-600">
              A tápérték-számítás ugyanabból a receptúrából jön, mint a gyártmánylap — nem kell külön nyilvántartást vezetni hozzá. Részletek: <Link to="/receptbol-kaloria-szamitas" style={{ color: TEAL, fontWeight: 600 }}>receptből kalória- és tápértékszámítás</Link>.
            </p>
          </div>

          <div id="bejelentes">
            <h2 className="text-2xl font-bold mb-4" style={{ color: DARK }}>6. NÉBIH bejelentés, működési engedély</h2>
            <p className="text-base leading-relaxed text-gray-600">
              Az élelmiszer-előállító és -forgalmazó tevékenységet a NÉBIH élelmiszerlánc-felügyeleti nyilvántartásába be kell jelenteni, a helyi önkormányzat/jegyző felé pedig a kereskedelmi vagy vendéglátó tevékenység bejelentése, illetve engedélyeztetése szükséges a nyitás előtt. Ez tevékenységtől és telephelytől függően eltérő eljárás, ezért ezt mindenképp érdemes a helyi jegyzővel vagy egy HACCP-tanácsadóval pontosítani, mielőtt kinyitsz.
            </p>
          </div>

          <div id="checklist">
            <h2 className="text-2xl font-bold mb-6" style={{ color: DARK }}>Összefoglaló checklist</h2>
            <div className="rounded-2xl p-6" style={{ background: BG, border: '1px solid rgba(91,184,160,0.2)' }}>
              <ul className="flex flex-col gap-3">
                {[
                  'Gyártmánylap (vagy anyaghányad-nyilvántartás) minden termékhez',
                  'HACCP-terv + napi vezetett napló (hűtő, takarítás, termelés)',
                  'Allergén jelölés minden pultban árult termékhez',
                  'Eredetigazolás sablon, ha közétkeztetésbe/rendezvényre szállítasz',
                  'Tápérték jelölés, ha előrecsomagolva, saját márkanévvel árulsz',
                  'NÉBIH bejelentés + önkormányzati/jegyzői engedélyeztetés',
                ].map(item => (
                  <li key={item} className="flex items-start gap-3">
                    <Check size={18} style={{ color: TEAL }} className="mt-0.5 shrink-0" />
                    <span className="text-sm text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

        </div>
      </article>

      <section style={{ background: BG }} className="py-20">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-10" style={{ color: DARK }}>Gyakori kérdések</h2>
          <FaqAccordion items={FAQS} />
        </div>
      </section>

      <section style={{ background: DARK }} className="py-20">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Az első négy pontot a CukrászDoc automatizálja</h2>
          <p className="mb-8 text-base" style={{ color: 'rgba(255,255,255,0.55)' }}>Gyártmánylap, HACCP napló, allergén jelölés és eredetigazolás — egyszer viszed fel a receptet, utána minden dokumentum ugyanabból az adatból készül.</p>
          <Link to="/register" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-base" style={{ background: TEAL, color: '#fff' }}>
            Ingyenes regisztráció <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
